import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceService } from '../services/api';
import './ApprovalQueue.css';

function ApprovalQueue({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [rejectComments, setRejectComments] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (user.role !== 'supervisor' && user.role !== 'administrador') {
      navigate('/');
      return;
    }
    loadInvoices();
  }, [user, navigate]);

  const loadInvoices = async () => {
    try {
      const response = await invoiceService.listInvoices();
      // Filter only pending approval invoices
      let pending = response.invoices.filter(inv => inv.status === 'pending_approval');
      // If supervisor, filter by approval limit
      if (user.role === 'supervisor' && user.approvalLimit) {
        pending = pending.filter(inv => inv.totalAmount <= user.approvalLimit);
      }
      setInvoices(pending);
    } catch (err) {
      setError('Error al cargar facturas pendientes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (invoiceId) => {
    if (!window.confirm('¿Está seguro de aprobar esta factura?')) {
      return;
    }

    try {
      setError('');
      await invoiceService.approveInvoice(invoiceId);
      await loadInvoices();
      setSelectedInvoice(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al aprobar factura');
      console.error(err);
    }
  };

  const handleReject = async (invoiceId) => {
    if (!rejectComments.trim()) {
      alert('Por favor ingrese los motivos del rechazo');
      return;
    }

    if (!window.confirm('¿Está seguro de rechazar esta factura?')) {
      return;
    }

    try {
      setError('');
      await invoiceService.rejectInvoice(invoiceId, rejectComments);
      await loadInvoices();
      setSelectedInvoice(null);
      setRejectComments('');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al rechazar factura');
      console.error(err);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('es-AR');
  };

  if (loading) {
    return <div className="container loading">Cargando facturas pendientes...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Cola de Aprobación</h1>
        <span className="badge badge-pending" style={{ fontSize: '16px', padding: '8px 16px' }}>
          {invoices.length} pendientes
        </span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {invoices.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>
            No hay facturas pendientes de aprobación
          </p>
        </div>
      ) : (
        <div className="approval-grid">
          {invoices.map((invoice) => (
            <div key={invoice.invoiceId} className="approval-card">
              <div className="approval-header">
                <h3>
                  Factura {invoice.tipoComprobante}-{invoice.puntoVenta}-{invoice.invoiceNumber}
                </h3>
                <span className="badge badge-pending">Pendiente</span>
              </div>

              <div className="approval-body">
                <div className="approval-detail">
                  <span className="label">Proveedor:</span>
                  <span className="value">{invoice.supplierName || 'No especificado'}</span>
                </div>
                <div className="approval-detail">
                  <span className="label">CUIT:</span>
                  <span className="value">{invoice.supplierCuit || 'No especificado'}</span>
                </div>
                <div className="approval-detail">
                  <span className="label">Fecha:</span>
                  <span className="value">{invoice.invoiceDate || formatDate(invoice.createdAt)}</span>
                </div>
                <div className="approval-detail">
                  <span className="label">Monto:</span>
                  <span className="value" style={{ fontSize: '18px', fontWeight: 'bold', color: '#667eea' }}>
                    {formatAmount(invoice.totalAmount)}
                  </span>
                </div>
                <div className="approval-detail">
                  <span className="label">Creado por:</span>
                  <span className="value">{invoice.createdBy}</span>
                </div>

                {invoice.items && invoice.items.length > 0 && (
                  <div className="approval-items">
                    <strong>Items ({invoice.items.length}):</strong>
                    <ul>
                      {invoice.items.slice(0, 3).map((item, index) => (
                        <li key={index}>
                          {item.description} - {item.quantity} x {formatAmount(item.unitPrice)}
                        </li>
                      ))}
                      {invoice.items.length > 3 && (
                        <li>... y {invoice.items.length - 3} más</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="approval-actions">
                <button
                  onClick={() => navigate(`/invoices/${invoice.invoiceId}`)}
                  className="btn btn-secondary btn-sm"
                >
                  Ver Detalle
                </button>
                
                <button
                  onClick={() => setSelectedInvoice(invoice.invoiceId)}
                  className="btn btn-danger btn-sm"
                >
                  Rechazar
                </button>
                
                <button
                  onClick={() => handleApprove(invoice.invoiceId)}
                  className="btn btn-success btn-sm"
                  disabled={user.role === 'supervisor' && user.approvalLimit && invoice.totalAmount > user.approvalLimit}
                  title={user.role === 'supervisor' && user.approvalLimit && invoice.totalAmount > user.approvalLimit ? 
                    `Monto supera tu límite de $${user.approvalLimit}` : ''}
                >
                  Aprobar
                </button>
              </div>

              {selectedInvoice === invoice.invoiceId && (
                <div className="reject-form">
                  <label>Motivos del rechazo:</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={rejectComments}
                    onChange={(e) => setRejectComments(e.target.value)}
                    placeholder="Describa los motivos del rechazo..."
                  />
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button
                      onClick={() => {
                        setSelectedInvoice(null);
                        setRejectComments('');
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => handleReject(invoice.invoiceId)}
                      className="btn btn-danger btn-sm"
                    >
                      Confirmar Rechazo
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ApprovalQueue;
